// Client-side helpers for policy-based authorization (UI gating only)
import { useState, useEffect } from "react";

export const Roles = {
  SuperAdmin: "SuperAdmin",
  StokrazeUser: "StokrazeUser",
  SalesRep: "SalesRep",
};

export const Policies = {
  Read: "Read",
  Write: "Write",
  Create: "Create",
  NoAccess: "NoAccess",
  FullAccess: "FullAccess",
};

// Mirror of server policy->roles mapping (keep in sync with backend)
const policyToRoles = {
  [Policies.Write]: [Roles.StokrazeUser, Roles.SuperAdmin],
  [Policies.Read]: [Roles.SalesRep, Roles.StokrazeUser, Roles.SuperAdmin],
  [Policies.Create]: [Roles.SalesRep, Roles.StokrazeUser, Roles.SuperAdmin],
  [Policies.NoAccess]: [],
  [Policies.FullAccess]: [Roles.SuperAdmin],
};

// Action-based permissions (UI helpers)
export const Permissions = {
  View: "view",
  Add: "add",
  Edit: "edit",
  Delete: "delete",
  Tenants: "tenants",
};

const permissionToRoles = {
  [Permissions.View]: [Roles.SalesRep, Roles.StokrazeUser, Roles.SuperAdmin],
  // General Add (products, categories, vendors) - SalesRep should not have this globally
  [Permissions.Add]: [Roles.StokrazeUser, Roles.SuperAdmin],
  [Permissions.Edit]: [Roles.StokrazeUser, Roles.SuperAdmin],
  [Permissions.Delete]: [Roles.StokrazeUser, Roles.SuperAdmin],
  [Permissions.Tenants]: [Roles.SuperAdmin],
};

export function can(action) {
  if (!action) return false;
  const roles = permissionToRoles[action] || [];
  if (!roles.length) return false;
  return hasAnyRole(roles);
}

function safeParseJwt(token) {
  try {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(decodeURIComponent(escape(atob(payload))));
    return json;
  } catch (e) {
    return null;
  }
}

export function getToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj?.token || null;
  } catch (e) {
    return null;
  }
}

export function getRolesFromToken(token) {
  const payload = safeParseJwt(token || getToken());
  if (!payload) return [];

  // common claim keys for roles
  const possibleKeys = [
    "role",
    "roles",
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/roles",
  ];

  for (const k of possibleKeys) {
    const v = payload[k];
    if (!v) continue;
    if (Array.isArray(v)) return v;
    if (typeof v === "string") {
      // roles sometimes serialized as single string or comma separated
      try {
        const maybeArray = JSON.parse(v);
        if (Array.isArray(maybeArray)) return maybeArray;
      } catch (e) {
        // not json - fallthrough
      }
      return v.split(",").map((r) => r.trim()).filter(Boolean);
    }
  }

  // Some servers put roles under 'http://schemas...' with a single value
  // also check for claim list in 'unique_name' style custom claim keys
  const claimRoles = Object.keys(payload)
    .filter((k) => k.toLowerCase().includes("role"))
    .map((k) => payload[k])
    .flat();

  if (claimRoles && claimRoles.length) {
    if (Array.isArray(claimRoles)) return claimRoles;
    if (typeof claimRoles === "string") return claimRoles.split(",").map((r) => r.trim()).filter(Boolean);
  }

  return [];
}

export function hasRole(role) {
  if (!role) return false;
  const token = getToken();
  if (!token) return false;
  const roles = getRolesFromToken(token);
  return roles.includes(role);
}

export function hasAnyRole(rolesToCheck = []) {
  if (!rolesToCheck || !rolesToCheck.length) return false;
  const token = getToken();
  if (!token) return false;
  const roles = getRolesFromToken(token);
  return rolesToCheck.some((r) => roles.includes(r));
}

export function hasPolicy(policy) {
  if (!policy) return false;
  const allowedRoles = policyToRoles[policy] || [];
  if (!allowedRoles.length) return false;
  return hasAnyRole(allowedRoles);
}

// React helper: conditionally render children when policy satisfied
export function RequirePolicy({ policy, children, fallback = null }) {
  return hasPolicy(policy) ? children : fallback;
}

// hook-style helper for functional components
export function useHasPolicy(policy) {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    setAllowed(hasPolicy(policy));
  }, [policy]);
  return allowed;
}
